<template>
    <div>
        <v-row>
            <v-col cols="4" v-for="({ text, value, formatter }) in totalCards" :key="text">
                <v-card height="127">
                    <v-card-subtitle class="">{{ text }}</v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <div v-if="loading" style="height: 100%" class="d-flex justify-center align-center">
                            <v-icon size="30px" class="fa-spin" >
                                fa-spinner
                            </v-icon>                    
                        </div>
                        <div v-else>
                            <div class="display-1 text--primary">{{ formatter(value(totals)) }}</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-card>
                    <v-card-text>
                        <loader v-if="loading" />
                        <vue-apex-charts v-else height="400px" type="treemap" :options="distributionChart.options" :series="distributionChart.series" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-if="team">
            <v-col>
                <v-card>
                    <v-card-text>
                        <loader v-if="loading" />
                        <vue-apex-charts v-else height="400px" type="bar" :options="chart.options" :series="chart.series" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <loader v-if="loading" />
                <v-card v-else>
                    <v-card-text>
                        <v-subheader>
                            <v-row>
                                <v-col>
                                    Navn
                                </v-col>
                                <v-col v-for="category in categories" :key="category" @click="setSort(category)">
                                    {{ category }}
                                    <v-icon v-if="sortField === category && sortOrder">fa-angle-down</v-icon>
                                    <v-icon v-else-if="sortField === category">fa-angle-up</v-icon>
                                </v-col>
                            </v-row>
                        </v-subheader>
                        <v-virtual-scroll height="70vh" item-height="64" :items="sortedAgents" bench="1000" class="striped-scroll">
                            <template v-slot:default="{ item: agent }">
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-row align="baseline">
                                            <v-col>
                                                {{ agent.name }}
                                            </v-col>
                                            <v-col v-for="category in categories" :key="category">
                                                {{ agent[category] }}
                                            </v-col>
                                        </v-row>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-virtual-scroll>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

export default {
	components: {
		VueApexCharts
	},
    data () {
        return {
            loading: true,
            agents: null,
            categories: null,
            sortField: 'Total',
            sortOrder: true,
            chart: {
                series: [],
                options: {
                    chart: {
                        stacked: true,
                        toolbar: {
                            show: false
                        }
                    },
                    xaxis: {
                        categories: []
                    },
                    legend: {
                        position: 'right',
                        showForZeroSeries: false
                    },
                    colors: ['#c10ac8', '#6fcaf9', '#135e1f', '#c8366f', '#b4be90', '#4c3d69', '#dcb8c3', '#d7b4a1', '#2c9e14'],
                    dataLabels: {
                        formatter: this.$options.filters.separator
                    },
                    tooltip: {
                        followCursor: true,
                        shared: false,
                        custom: function({ series, dataPointIndex }) {
                            let currentTotal = 0
                            series.forEach((s) => {
                            currentTotal += s[dataPointIndex]
                            })
                            return '<div class="stacked-tooltip">' +
                            '<span><b>Total: </b>' + currentTotal + '</span>' +
                            '</div>'
                        }
                    }
                }
            },
            distributionChart: {
                options: {
                    chart: {
                        toolbar: {
                            show: false
                        }
                    },
                    plotOptions: {
                        treemap: {
                            distributed: true,
                            enableShades: false
                        }
                    },
                    legend: {
                        show: false
                    },
                    colors: ['#c10ac8', '#6fcaf9', '#135e1f', '#c8366f', '#b4be90', '#4c3d69', '#dcb8c3', '#d7b4a1', '#2c9e14'],
                    dataLabels: {
                        formatter: ((category, { value, w: { globals: { seriesTotals: [ total ]} } }) => category + ': ' + this.$options.filters.percentage(value / (total || 1)))
                    }
                },
                series: []
            }
        }
    },
    computed: {
        totalCards () {
            if (!this.categories) return null
            return this.categories.map(category => ({
                text: category,
                value: (stats) => stats[category],
                formatter: this.$options.filters.separator
            }))
        },
        filteredAgents () {
            if (!this.agents) return []
            return this.agents.filter(agent => {
                return agent.name.toLowerCase().includes(this.$store.state.quality.searchString.toLowerCase())
            })
        },
        sortedAgents () {
            const order = this.sortOrder ? -1 : 1
            return [...this.filteredAgents].sort((a, b) => a[this.sortField] > b[this.sortField] ? order : -order)
        },
        department () {
            return this.$store.getters['quality/department']
        },
        year () {
            return this.$store.state.quality.year
        },
        startWeek () {
            return this.$store.state.quality.startWeek
        },
        endWeek () {
            return this.$store.state.quality.endWeek
        },
        ytd () {
            return this.$store.state.quality.ytd
        },
        inactive () {
            return this.$store.state.quality.inactive
        },
        team () {
            return this.$store.state.quality.team
        },
        searchString () {
            return this.$store.state.quality.searchString
        },
        totals () {
            return this.$reduceNested(this.agents, this.categories)
        },
    },
    methods: {
		setSort(field) {
			if (this.sortField === field) this.sortOrder = !this.sortOrder;
			else {
                this.sortField = field
                this.sortOrder = true
            }
		},
        async getData () {
            this.loading = true
            const response = await this.$http().get('quality/status/complaints', {
                params: {
                    year: this.$store.state.quality.year,
                    startWeek: this.$store.state.quality.startWeek,
                    endWeek: this.$store.state.quality.endWeek,
                    team: this.team,
                    department: this.department,
                    inactive: this.$store.state.quality.inactive
                }
            })
            this.agents = response.data.stats
            this.categories = response.data.categories
            this.chart.options.xaxis.categories = response.data.labels
            this.chart.series = response.data.series
            this.distributionChart.series = response.data.distributionSeries
            this.loading = false
        }
    },
    watch: {
        selectedAgent: function () {
            this.getComplaints(this.selectedAgent)
        },
        department: function () {
            this.getData()
        },
        year: function () {
            this.getData()
        },
        startWeek: function () {
            this.getData()
        },
        endWeek: function () {
            this.getData()
        },
        ytd: function () {
            this.getData()
        },
        inactive () {
            this.getData()
        },
        team: function () {
            this.getData()
        }
    },
    created () {
        this.$store.state.quality.searchString = ''
        this.getData()
    }
}
</script>

<style lang="scss">
.stacked-tooltip {
    padding: 10px;
}
</style>